import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { WeatherSnapshot } from '../engine/weather.ts';
import { isWeatherSnapshot } from '../engine/weather.ts';

const KEY = 'festival-public-weather-v1';
function readSaved(): WeatherSnapshot | null {
  try { const saved: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null'); return isWeatherSnapshot(saved) ? saved : null; }
  catch { return null; }
}
const WeatherContext = createContext<{ snapshot: WeatherSnapshot | null; now: number; loading: boolean; offline: boolean; failed: boolean; refresh: () => void } | null>(null);
export function WeatherProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState(readSaved);
  const [now, setNow] = useState(Date.now);
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [failed, setFailed] = useState(false);
  const active = useRef<AbortController | null>(null);
  const refresh = useCallback(() => {
    setNow(Date.now()); setOffline(!navigator.onLine);
    if (document.hidden || !navigator.onLine || active.current) return;
    const controller = new AbortController(); active.current = controller; setLoading(true);
    const timeout = window.setTimeout(() => controller.abort(), 22_000);
    void fetch('/api/weather', { cache: 'no-store', signal: controller.signal })
      .then(async r => { if (!r.ok) throw new Error('Unavailable'); const data: unknown = await r.json(); if (!isWeatherSnapshot(data)) throw new Error('Incomplete weather response'); return data; })
      .then(data => { setSnapshot(data); setFailed(false); try { localStorage.setItem(KEY, JSON.stringify(data)); } catch { /* Optional public snapshot. */ } })
      .catch(() => { setFailed(true); })
      .finally(() => { window.clearTimeout(timeout); if (active.current === controller) { active.current = null; setLoading(false); } });
  }, []);
  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh); window.addEventListener('offline', refresh);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); window.removeEventListener('online', refresh); window.removeEventListener('offline', refresh); active.current?.abort(); active.current = null; };
  }, [refresh]);
  return <WeatherContext.Provider value={{ snapshot, now, loading, offline, failed, refresh }}>{children}</WeatherContext.Provider>;
}
export function useWeather() {
  const context = useContext(WeatherContext);
  if (!context) throw new Error('WeatherProvider required');
  return context;
}
